import { Box, Typography } from '@material-ui/core';
import { DepartmentCostSummary, Team } from '@internal/schemas-common';
import { StackedBarChart } from '@internal/ui-common';
import { red } from '@material-ui/core/colors';
import { useMemo } from 'react';

interface CostStackedBarChartProps {
  costSummary: DepartmentCostSummary;
  teams: Team[];
}

export const CostStackedBarChart = ({
  costSummary,
  teams,
}: CostStackedBarChartProps) => {
  const chartData = useMemo(() => {
    const validTeams = teams.filter(team => team && team.nameInGitlab);
    const teamMap = Object.fromEntries(
      validTeams.map(team => [team.nameInGitlab, team]),
    );
    const existingTeamNames = new Set(
      costSummary.teams.map(t => t.teamNamePortal),
    );

    const allTeamsData = [
      ...costSummary.teams.map(t => ({
        teamName: teamMap[t.teamNamePortal]?.name || t.teamNamePortal,
        team: teamMap[t.teamNamePortal] as Team | undefined,
        overspend: t.overspend ?? 0,
      })),
      ...validTeams
        .filter(team => !existingTeamNames.has(team.nameInGitlab))
        .map(team => ({
          teamName: team.name,
          team: team as Team | undefined,
          overspend: 0,
        })),
    ];

    allTeamsData.sort((a, b) => b.overspend - a.overspend);

    return allTeamsData;
  }, [costSummary.teams, teams]);

  const series = useMemo(
    () => [
      {
        data: chartData.map(d => d.overspend),
        label: 'Overspend',
        barLabel: 'value' as const,
      },
    ],
    [chartData],
  );

  if (chartData.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="body1">No cost data available</Typography>
      </Box>
    );
  }

  return (
    <StackedBarChart
      chartData={chartData}
      series={series}
      routeSuffix="cost"
      colors={[red[300]]}
      xAxisLabel="Overspend (€)"
    />
  );
};
